import "@styles/custom.css";
import { CartProvider } from "react-use-cart";
import "../styles/style.css";

//internal import
import React, { useEffect } from "react"; // Combined React imports
import PropTypes from 'prop-types'; // Added PropTypes import
import { UserProvider } from "@context/UserContext";
import DefaultSeo from "@component/common/DefaultSeo";
import { SidebarProvider } from "@context/SidebarContext";
import { CustomCartProvider } from "@context/CartContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import ChatwootWidget from "@component/chat/ChatwootWidget";
import { Windmill } from "@windmill/react-ui";
import myTheme from "src/theme/myTheme";
import Cookies from 'js-cookie';
import useFcmToken from '@hooks/useFcmToken';

//Analytics for Google
import Script from 'next/script'
import { useRouter, default as Router } from 'next/router'; // Combined router imports
import * as gtag from "../lib/gtag"
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { PagesProgressProvider as ProgressProvider } from '@bprogress/next'

React.useLayoutEffect = React.useEffect;

// Create a custom event to trigger PostHog identification
const identifyUserEvent = new Event('identify_posthog_user');

// Add prop types validation
function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const { fcmToken } = useFcmToken(); // Get FCM token for PostHog identification

  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);  // Function to identify user in PostHog
  const identifyUser = () => {
    const userInfoStr = Cookies.get('userInfo');
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo && userInfo.email) {
          const identificationProps = {
            id: userInfo._id || null,
            email: userInfo.email,
            name: userInfo.name,
            verified_through: userInfo.verfied_through || 'Unknown',
            address: userInfo.address || null,
            phone: userInfo.phone || null,
            avatar: userInfo.image || null,
            order_count: userInfo.order_count || 0,
            customer_value: userInfo.customer_value || 0
          };

          // Add FCM token and its metadata if available
          if (fcmToken) {
            identificationProps.fcm_token = fcmToken;
            
            // Get FCM token storage info for validity and timestamp
            try {
              const tokenStorageKey = 'fcm_token_storage';
              const storedTokenData = localStorage.getItem(tokenStorageKey);
              
              if (storedTokenData) {
                const tokenData = JSON.parse(storedTokenData);
                const tokenTimestamp = tokenData.timestamp;
                const tokenAge = Date.now() - tokenTimestamp;
                const isTokenValid = tokenAge < 7 * 24 * 60 * 60 * 1000; // 7 days
                  identificationProps.fcm_token_timestamp = new Date(tokenTimestamp).toISOString();
                identificationProps.fcm_token_age_days = Math.floor(tokenAge / (24 * 60 * 60 * 1000));
                identificationProps.fcm_token_is_valid = isTokenValid;
                identificationProps.fcm_token_permission = tokenData.permission || 'unknown';
              } else {                // Token exists but no storage data - likely fresh token
                identificationProps.fcm_token_timestamp = new Date().toISOString();
                identificationProps.fcm_token_age_days = 0;
                identificationProps.fcm_token_is_valid = true;
                identificationProps.fcm_token_permission = Notification?.permission || 'unknown';
              }
            } catch (storageError) {              console.warn('📊 PostHog: Could not read FCM token storage data:', storageError);
              // Fallback values if storage read fails
              identificationProps.fcm_token_timestamp = new Date().toISOString();
              identificationProps.fcm_token_age_days = 0;
              identificationProps.fcm_token_is_valid = true;
              identificationProps.fcm_token_permission = Notification?.permission || 'unknown';
            }
          }

          posthog.identify(userInfo.email, identificationProps);
          console.log('📊 PostHog: User identified with properties:', identificationProps);
        }
      } catch (error) {
        console.error('Error parsing user info for PostHog identification:', error);
      }
    }
  };

  useEffect(() => {    // Initialize PostHog
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: "/ingest",
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      disable_web_experiments: false,
      opt_in_site_apps: true,
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') posthog.debug()
      }
    })

    // Initial identification check
    identifyUser();

    // Add listener for custom event from login flows
    window.addEventListener('identify_posthog_user', identifyUser);

    const handleRouteChange = () => posthog?.capture('$pageview');
    Router.events.on('routeChangeComplete', handleRouteChange); return () => {
      window.removeEventListener('identify_posthog_user', identifyUser);
      Router.events.off('routeChangeComplete', handleRouteChange);
    }
  }, [])

  // Re-identify user when FCM token changes
  useEffect(() => {
    if (fcmToken) {
      console.log('📊 PostHog: FCM token available, re-identifying user...');
      identifyUser();
    }
  }, [fcmToken]);

  return (
    <>
      <Script strategy="afterInteractive" src="https://www.googletagmanager.com/gtag/js?id=G-4G3K0DXTV8" />
      <Script
        id='google-analytics'
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-4G3K0DXTV8', {
page_path: window.location.pathname,
});
`,
        }}
      />      <PostHogProvider client={posthog}>
        <ProgressProvider
          height="2px"
          color="#ff0000"
          options={{ showSpinner: false }}
          shallowRouting
        >
          <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}>
            <UserProvider>
              <SidebarProvider>
                <CustomCartProvider>
                  <CartProvider>
                    <DefaultSeo />
                    <ChatwootWidget />
                    <Windmill usePreferences theme={myTheme}>
                      <Component {...pageProps} />
                    </Windmill>
                  </CartProvider>
                </CustomCartProvider>
              </SidebarProvider>
            </UserProvider>
          </GoogleOAuthProvider>
        </ProgressProvider>
      </PostHogProvider>
    </>
  );
}

// Add prop validation
MyApp.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired
};

export default MyApp;
